import { useAccount } from 'wagmi';
import { useQuery } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import { INDEXER_API_URL } from 'src/constants';
import {
  ApiWithdrawalRequest,
  ApiProtocolInfo,
  ApiWalletTransaction,
} from 'src/types';

export function useWalletRequests() {
  const { address } = useAccount();

  const {
    data: _walletRequests = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['walletRequests', address],
    queryFn: async (): Promise<ApiWithdrawalRequest[]> => {
      const response = await fetch(
        `${INDEXER_API_URL}/withdrawal_requests?address=${address}`
      );

      return response.json();
    },
    enabled: !!address,
  });

  return {
    walletRequests: Array.isArray(_walletRequests) ? _walletRequests : [],
    isLoadingWalletRequests: isLoading,
    walletRequestsError: error,
  };
}

export function useProtocolInfo({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const {
    data: protocolInfo,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['protocolInfo'],
    queryFn: async (): Promise<ApiProtocolInfo> => {
      const response = await fetch(`${INDEXER_API_URL}/info`);
      return response.json();
    },
    enabled,
  });

  return {
    protocolInfo,
    isLoadingProtocolInfo: isLoading,
    protocolInfoError: error,
  };
}

export function useLifetimeRewards() {
  const { address } = useAccount();

  const {
    data: lifetimeRewards = '0',
    isLoading,
    error,
  } = useQuery({
    queryKey: ['lifetimeRewards', address],
    queryFn: async (): Promise<string> => {
      const response = await fetch(
        `${INDEXER_API_URL}/lifetime_rewards?address=${address}`
      );

      return response.json();
    },
    enabled: !!address,
  });

  return {
    lifetimeRewards,
    lifetimeRewardsFormatted: BigNumber(lifetimeRewards).div(1e18).toFixed(),
    isLoadingLifetimeRewards: isLoading,
    lifetimeRewardsError: error,
  };
}

export function useWalletTransactions() {
  const { address } = useAccount();

  const {
    data: walletTransactions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['walletTransactions', address],
    queryFn: async (): Promise<ApiWalletTransaction[]> => {
      const response = await fetch(
        `${INDEXER_API_URL}/transactions?address=${address}`
      );

      return response.json();
    },
    enabled: !!address,
  });

  return {
    walletTransactions,
    isLoadingWalletTransactions: isLoading,
    walletTransactionsError: error,
  };
}
